/**
 * Simple, reliable in-iframe preview.
 *
 * All TSX/JSX files are concatenated (import statements stripped, since React
 * and ReactDOM are UMD globals in the iframe), then transpiled once by Babel
 * Standalone. This avoids ESM importmap+blob URL quirks entirely and matches
 * the classic CodePen/JSFiddle approach.
 *
 * Downside vs a real bundler: cross-file imports are best-effort (relative
 * paths are stripped; all top-level exports are hoisted into the same scope).
 * That's plenty for a preview.
 */

function guessEntry(files) {
  return [
    "src/main.tsx",
    "src/main.ts",
    "src/main.jsx",
    "src/main.js",
    "src/index.tsx",
    "src/index.jsx",
    "src/App.tsx",
  ].find((k) => files[k] !== undefined);
}

const SUPPORTED = /\.(t|j)sx?$/;
const EXCLUDE = /^(vite\.config|tailwind\.config|postcss\.config|package|tsconfig|\.eslintrc)/;

function stripImports(source) {
  // Drop every `import ... from "..."` statement — React/ReactDOM come from globals.
  return source
    .replace(/^\s*import\s+[^;]*?;?\s*$/gm, "")
    .replace(/^\s*import\s+[^;]*from\s+["'][^"']+["'];?\s*$/gm, "")
    .replace(/^\s*import\s*\(["'][^"']+["']\);?\s*$/gm, "");
}

function stripExports(source) {
  // Convert `export default` → variable assignment, `export const/function` → keep decl.
  return source
    .replace(/export\s+default\s+function\s+(\w+)/g, "function $1")
    .replace(/export\s+default\s+/g, "const __default = ")
    .replace(/export\s+/g, "");
}

export async function bundleForPreview(files) {
  const entry = guessEntry(files);
  if (!entry) throw new Error("No entry file — expected src/main.tsx or src/App.tsx.");

  // Concatenate every source file (entry LAST so top-level side effects run).
  const orderedPaths = Object.keys(files)
    .filter((p) => SUPPORTED.test(p) && !EXCLUDE.test(p) && p !== entry)
    .concat([entry]);

  const combined = orderedPaths
    .map((p) => {
      const src = stripExports(stripImports(files[p]));
      return `\n// === ${p} ===\n${src}`;
    })
    .join("\n");

  const cssBlocks = Object.entries(files)
    .filter(([p]) => p.endsWith(".css"))
    .map(([, c]) => `<style>${c}</style>`)
    .join("");

  const boot = `
    const source = ${JSON.stringify(combined)};
    async function ensureBabel() {
      if (window.Babel) return;
      await new Promise((res, rej) => {
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/@babel/standalone@7.24.7/babel.min.js';
        s.onload = res; s.onerror = () => rej(new Error('Failed to load Babel'));
        document.head.appendChild(s);
      });
    }
    function reportError(err) {
      const msg = err && err.stack ? err.stack : (err && err.message ? err.message : String(err));
      const root = document.getElementById('root') || document.body;
      root.innerHTML = '<pre style="color:#dc2626;padding:16px;font:12px/1.4 monospace;white-space:pre-wrap;background:#fff1f2;">' + msg + '</pre>';
      parent.postMessage({ type: 'omnicraft:preview-error', error: msg }, '*');
    }
    (async () => {
      try {
        await ensureBabel();
        const { code } = Babel.transform(source, {
          presets: [
            ['typescript', { onlyRemoveTypeImports: true, allExtensions: true, isTSX: true }],
            ['react', { runtime: 'classic' }],
          ],
          filename: 'preview.tsx',
        });
        // React/ReactDOM globals + ReactDOM.createRoot alias so imported main.tsx code just works.
        const wrapped = 'const React = window.React;\\n' +
                        'const { useState, useEffect, useReducer, useRef, useMemo, useCallback, useContext, createContext, Fragment } = React;\\n' +
                        'const ReactDOM = window.ReactDOM;\\n' +
                        'const createRoot = ReactDOM.createRoot || (el => ReactDOM.render && { render: (n) => ReactDOM.render(n, el) });\\n' +
                        code;
        new Function(wrapped)();
      } catch (e) { reportError(e); }
    })();
    window.addEventListener('error', (e) => reportError(e.error || e.message));
    window.addEventListener('unhandledrejection', (e) => reportError(e.reason));
  `;

  const baseHtml =
    files["index.html"] ||
    `<!doctype html><html><head><meta charset="UTF-8"/><script src="https://cdn.tailwindcss.com"></script></head><body><div id="root"></div></body></html>`;
  const scrubbed = baseHtml.replace(/<script[^>]*type="module"[^>]*>[\s\S]*?<\/script>/g, "");
  const reactUmd = `<script crossorigin src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
<script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>`;
  return scrubbed
    .replace("</head>", `${reactUmd}${cssBlocks}</head>`)
    .replace("</body>", `<script>${boot}</script></body>`);
}
